import { useCallback, useRef, useState } from 'react'
import type { OfApi } from './ofApi'
import { toUser, type OfUser } from './posts'

/**
 * Lists come with `skip_users=all` (authors as bare ids); the OnlyFans page
 * then asks `users/list?a[]=…` once for the ids it has not seen yet.
 */
export function useUsers(of: OfApi, referer: string) {
  const [users, setUsers] = useState<Record<number, OfUser>>({})
  const asked = useRef(new Set<number>())

  const resolve = useCallback(
    async (ids: number[]) => {
      const todo = [...new Set(ids)].filter((id) => id > 0 && !asked.current.has(id))
      if (!todo.length) return
      todo.forEach((id) => asked.current.add(id))
      try {
        const payload = await of.get<Record<string, Record<string, unknown>>>('users/list', { 'a[]': todo }, referer)
        const found: Record<number, OfUser> = {}
        for (const raw of Object.values(payload ?? {})) {
          const u = toUser(raw)
          if (u.id) found[u.id] = u
        }
        setUsers((all) => ({ ...all, ...found }))
      } catch {
        todo.forEach((id) => asked.current.delete(id))
      }
    },
    [of, referer],
  )

  const remember = useCallback((u: OfUser) => {
    asked.current.add(u.id)
    setUsers((all) => ({ ...all, [u.id]: u }))
  }, [])

  return { users, resolve, remember }
}
